import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { serviceurl } from 'src/app/constant/constant';
import { AuthTokenService } from 'src/app/services/auth-token.service';
import { SessionService } from 'src/app/services/session.service';

/** Marks a request already retried after renewing the token (retry once only). */
const RETRIED_AFTER_RENEWAL = new HttpContextToken<boolean>(() => false);

/** Sign-in and renewal calls carry their own credentials, never the token. */
const AUTH_ENDPOINTS = [
  'api/auth/signup/',
  'api/auth/verify-email/',
  'api/auth/resend-verification/',
  'api/auth/login/',
  'api/auth/password/first-change/',
  'api/auth/password/forgot/',
  'api/auth/password/reset/',
  'Api/token/',
]; // api/auth/password/change/ is signed: it's for signed-in users

/**
 * Sends the app's login token with every request to our backend (the whole
 * API, not just the store), so the backend knows who is asking:
 *
 *  - a token about to expire is renewed first (AuthTokenService);
 *  - if the backend still answers 401, the token is renewed once and the
 *    request repeated;
 *  - when it can't be renewed any more, the session ends (SessionService).
 *
 * Requests to other servers (Stripe…) never get the token.
 * (The class keeps its old name so the app module wiring is unchanged.)
 */
@Injectable()
export class MarketplaceAuthInterceptor implements HttpInterceptor {
  constructor(private tokens: AuthTokenService, private session: SessionService) {}

  private isOurBackend(url: string): boolean {
    return !!serviceurl && url.startsWith(serviceurl) && !AUTH_ENDPOINTS.some((path) => url.includes(path));
  }

  private withToken(req: HttpRequest<any>, token: string, retried = false): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      context: retried ? req.context.set(RETRIED_AFTER_RENEWAL, true) : req.context,
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isOurBackend(req.url) || req.headers.has('Authorization')) {
      return next.handle(req);
    }
    if (!this.tokens.accessToken && !this.tokens.refreshToken) {
      return next.handle(req); // not signed in
    }

    return this.tokens.validAccessToken().pipe(
      switchMap((token) => {
        if (!token) {
          // Expired and can't be renewed: end a stale sign-in, send unsigned.
          if (this.session.signedIn && !this.tokens.hasSession) this.session.end();
          return next.handle(req);
        }
        return next.handle(this.withToken(req, token)).pipe(
          catchError((err: unknown) => this.handleUnauthorized(err, req, next)),
        );
      }),
    );
  }

  /** A 401 with a token we thought was fine: renew once and repeat the request. */
  private handleUnauthorized(err: unknown, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const unauthorized = err instanceof HttpErrorResponse && err.status === 401;
    if (!unauthorized || req.context.get(RETRIED_AFTER_RENEWAL)) {
      return throwError(() => err);
    }
    if (!this.tokens.canRefresh) {
      if (this.session.signedIn) this.session.end();
      return throwError(() => err);
    }
    return this.tokens.renew().pipe(
      switchMap((fresh) => next.handle(this.withToken(req, fresh, true))),
      catchError((renewErr: unknown) => {
        // The renewal itself failed: the session is over.
        if (renewErr === err || !(renewErr instanceof HttpErrorResponse) || renewErr.url?.includes('Api/token/')) {
          this.session.end();
          return throwError(() => err);
        }
        return throwError(() => renewErr); // the repeated request failed for its own reasons
      }),
    );
  }
}
